import { ModFile, ModpackManifest, ModLoader, ModpackProfile } from '../types';

export const cleanModName = (fileName: string): string => {
  return fileName
    .replace(/\.jar$/i, '')
    // Remove common forge/fabric prefixes if repeated
    .replace(/^(fabric|forge|neoforge|quilt)[-_]/i, '')
    // Remove version numbers (v1.0.0, 1.20.1, +build, etc)
    .replace(/[-_]v?\d+(\.\d+)*.*$/i, '')
    // Remove common separators
    .replace(/[-_]/g, ' ')
    // Remove brackets and parenthesis
    .replace(/[\[\]\(\)]/g, '')
    .trim();
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const computeSHA1 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Recursively scans FileSystemEntry from drag-and-drop items to extract all .jar files
 * Handles both individual files and dropped folders (.minecraft/mods)
 */
export const extractJarFilesFromDataTransfer = async (
  dataTransfer: DataTransfer
): Promise<File[]> => {
  const jarFiles: File[] = [];

  // Check if WebkitGetAsEntry is available for directory traversal
  const items = dataTransfer.items;
  if (items && items.length > 0) {
    const queue: Promise<void>[] = [];

    const traverseEntry = (entry: any): Promise<void> => {
      return new Promise((resolve) => {
        if (!entry) {
          resolve();
          return;
        }

        if (entry.isFile) {
          entry.file((file: File) => {
            if (file && file.name.toLowerCase().endsWith('.jar')) {
              jarFiles.push(file);
            }
            resolve();
          }, () => resolve());
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          const readAllEntries = () => {
            dirReader.readEntries((entries: any[]) => {
              if (entries.length === 0) {
                resolve();
              } else {
                const subPromises = entries.map(subEntry => traverseEntry(subEntry));
                Promise.all(subPromises).then(() => {
                  // Keep reading in case directory has >100 files (readEntries batching)
                  readAllEntries();
                });
              }
            }, () => resolve());
          };
          readAllEntries();
        } else {
          resolve();
        }
      });
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
        if (entry) {
          queue.push(traverseEntry(entry));
        } else {
          const file = item.getAsFile();
          if (file && file.name.toLowerCase().endsWith('.jar')) {
            jarFiles.push(file);
          }
        }
      }
    }

    if (queue.length > 0) {
      await Promise.all(queue);
    }
  }

  // Fallback to standard files array if no files detected via entry API
  if (jarFiles.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      const file = dataTransfer.files[i];
      if (file.name.toLowerCase().endsWith('.jar')) {
        jarFiles.push(file);
      }
    }
  }

  return jarFiles;
};

/**
 * Formats mod list for sharing across Discord / GitHub / Markdown
 */
export const formatModListText = (
  mods: ModFile[],
  mcVersion: string,
  loader: ModLoader,
  format: 'plain' | 'markdown' | 'discord' = 'markdown'
): string => {
  const timestamp = new Date().toLocaleDateString();

  if (format === 'discord') {
    let out = `**Minecraft Modpack** (${loader.toUpperCase()} ${mcVersion}) — ${mods.length} mods\n\`\`\`\n`;
    mods.forEach((m, idx) => {
      const statusIcon = m.status === 'found' ? '[UPDATE]' : m.status === 'missing' ? '[NOT FOUND]' : '[OK]';
      out += `${(idx + 1).toString().padStart(2, ' ')}. ${m.name} ${m.fileName ? `→ ${m.fileName}` : ''} ${statusIcon}\n`;
    });
    out += `\`\`\`\n*Generated by https://mcmods.sednium.com on ${timestamp}*`;
    return out;
  }

  if (format === 'markdown') {
    let out = `## Minecraft Modpack Manifest\n\n`;
    out += `- **Game Version:** \`${mcVersion}\`\n`;
    out += `- **Mod Loader:** \`${loader}\`\n`;
    out += `- **Total Mods:** ${mods.length}\n`;
    out += `- **Export Date:** ${timestamp}\n\n`;
    out += `| # | Mod Name | Status | Target File | Source |\n`;
    out += `|---|---|---|---|---|\n`;
    mods.forEach((m, idx) => {
      const status = m.status === 'found' ? 'Update Available' : m.status === 'missing' ? 'Not Found' : 'Up to Date';
      const file = m.fileName || m.originalFile?.name || '-';
      const source = m.source || 'Modrinth';
      out += `| ${idx + 1} | ${m.name} | ${status} | ${file} | ${source} |\n`;
    });
    out += `\n*Managed via [MC Mod Updater](https://mcmods.sednium.com)*\n`;
    return out;
  }

  // Plain text format
  let out = `Minecraft Modpack (${loader.toUpperCase()} ${mcVersion}) - ${mods.length} mods\n`;
  out += `Exported: ${timestamp}\n\n`;
  mods.forEach((m, idx) => {
    out += `${idx + 1}. ${m.name}${m.fileName ? ` (${m.fileName})` : ''}\n`;
  });
  return out;
};

/**
 * Builds a JSON export manifest
 */
export const buildModpackManifest = (
  mods: ModFile[],
  mcVersion: string,
  loader: ModLoader,
  packName: string = 'My Modpack'
): ModpackManifest => {
  return {
    manifestVersion: 1,
    name: packName,
    targetMcVersion: mcVersion,
    targetLoader: loader,
    exportedAt: new Date().toISOString(),
    mods: mods.map(m => ({
      name: m.name,
      projectId: m.projectId,
      versionNumber: m.versionNumber,
      fileName: m.fileName || m.originalFile?.name,
      sha1: m.sha1,
      isPinned: m.isPinned,
      customLoader: m.customLoader,
      customVersion: m.customVersion,
      source: m.source
    }))
  };
};

/**
 * Validates and parses an imported modpack JSON file
 */
export const parseModpackManifest = (jsonString: string): ModpackManifest | null => {
  try {
    const data = JSON.parse(jsonString);
    if (!data || !Array.isArray(data.mods)) {
      return null;
    }
    return {
      manifestVersion: data.manifestVersion || 1,
      name: data.name || 'Imported Modpack',
      targetMcVersion: data.targetMcVersion || '1.20.1',
      targetLoader: data.targetLoader || 'fabric',
      exportedAt: data.exportedAt || new Date().toISOString(),
      mods: data.mods.map((m: any) => ({
        name: m.name || 'Unnamed Mod',
        projectId: m.projectId,
        versionNumber: m.versionNumber,
        fileName: m.fileName,
        sha1: m.sha1,
        isPinned: Boolean(m.isPinned),
        customLoader: m.customLoader,
        customVersion: m.customVersion,
        source: m.source || 'modrinth'
      }))
    };
  } catch (e) {
    return null;
  }
};

/**
 * Formats a saved profile's mod list as Markdown/Discord/Plain text
 */
export const formatProfileModListText = (
  profile: ModpackProfile,
  format: 'plain' | 'markdown' | 'discord' = 'markdown'
): string => {
  const timestamp = new Date().toLocaleDateString();

  if (format === 'discord') {
    let out = `**Minecraft Modpack Profile: ${profile.name}** (${profile.loader.toUpperCase()} ${profile.mcVersion}) — ${profile.mods.length} mods\n\`\`\`\n`;
    profile.mods.forEach((m, idx) => {
      out += `${(idx + 1).toString().padStart(2, ' ')}. ${m.name}${m.fileName ? ` (${m.fileName})` : ''} ${m.isPinned ? '[PINNED]' : ''}\n`;
    });
    out += `\`\`\`\n*Exported via https://mcmods.sednium.com on ${timestamp}*`;
    return out;
  }

  if (format === 'markdown') {
    let out = `## ${profile.name} (Minecraft Modpack Profile)\n\n`;
    out += `- **Game Version:** \`${profile.mcVersion}\`\n`;
    out += `- **Mod Loader:** \`${profile.loader.toUpperCase()}\`\n`;
    out += `- **Total Mods:** ${profile.mods.length}\n`;
    out += `- **Export Date:** ${timestamp}\n\n`;
    out += `| # | Mod Name | File / Notes | Pinned |\n`;
    out += `|---|---|---|---|\n`;
    profile.mods.forEach((m, idx) => {
      out += `| ${idx + 1} | ${m.name} | ${m.fileName || '-'} | ${m.isPinned ? 'Yes' : 'No'} |\n`;
    });
    out += `\n*Managed via [MC Mod Updater](https://mcmods.sednium.com)*\n`;
    return out;
  }

  // Plain text
  let out = `Profile: ${profile.name} (${profile.loader.toUpperCase()} ${profile.mcVersion}) - ${profile.mods.length} mods\n`;
  out += `Exported: ${timestamp}\n\n`;
  profile.mods.forEach((m, idx) => {
    out += `${idx + 1}. ${m.name}${m.fileName ? ` (${m.fileName})` : ''} ${m.isPinned ? '[PINNED]' : ''}\n`;
  });
  return out;
};

/**
 * Generates a direct sharable web link for a profile
 */
export const generateProfileShareUrl = (profile: ModpackProfile): string => {
  try {
    const minified = {
      n: profile.name,
      v: profile.mcVersion,
      l: profile.loader,
      t: profile.versionType,
      m: profile.mods.map(mod => ({
        n: mod.name,
        p: mod.projectId,
        f: mod.fileName,
        pin: mod.isPinned ? 1 : undefined,
        cl: mod.customLoader,
        cv: mod.customVersion,
      })),
    };
    const jsonStr = JSON.stringify(minified);
    const base64 = btoa(unescape(encodeURIComponent(jsonStr)));
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://mcmods.sednium.com';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    return `${origin}${pathname}?profile=${encodeURIComponent(base64)}`;
  } catch {
    return '';
  }
};

/**
 * Decodes a shared profile from URL param string
 */
export const decodeProfileShareUrl = (encoded: string): ModpackProfile | null => {
  try {
    const jsonStr = decodeURIComponent(escape(atob(decodeURIComponent(encoded))));
    const data = JSON.parse(jsonStr);
    if (!data) return null;

    const name = data.n || data.name || 'Shared Modpack';
    const mcVersion = data.v || data.mcVersion || '1.21.4';
    const loader = (data.l || data.loader || 'fabric') as ModLoader;
    const versionType = (data.t || data.versionType || 'release') as 'release' | 'snapshot';
    const rawMods = data.m || data.mods || [];

    return {
      id: Math.random().toString(36).substring(2, 9),
      name,
      mcVersion,
      loader,
      versionType,
      mods: rawMods.map((m: any) => ({
        name: m.n || m.name || 'Unnamed Mod',
        projectId: m.p || m.projectId,
        fileName: m.f || m.fileName,
        isPinned: Boolean(m.pin || m.isPinned),
        customLoader: m.cl || m.customLoader,
        customVersion: m.cv || m.customVersion,
      })),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  } catch {
    return null;
  }
};